import type { ElementType, HTMLAttributes } from 'react'
import { cls } from './cls'

const SIZE = {
  sm: 'text-[10px] tracking-widest',
  md: 'text-[11px] tracking-[0.06em]',
}

const WEIGHT = { medium: 'font-medium', semibold: 'font-semibold' }

const GAP = { none: '', sm: 'mb-2', md: 'mb-3' }

/** Tags an eyebrow is allowed to render. Headings are here because a section
 *  label is often genuinely a heading, and rendering it as a div throws that
 *  away for anyone navigating by landmark. */
type EyebrowTag = 'div' | 'span' | 'p' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'legend'

export interface EyebrowProps extends HTMLAttributes<HTMLElement> {
  as?: EyebrowTag
  size?: keyof typeof SIZE
  /** Defaults to semibold at the small size and medium at the larger one,
   *  which is how the two were used before they became a component. */
  weight?: keyof typeof WEIGHT
  gap?: keyof typeof GAP
}

/** An uppercase micro-heading labelling a section. */
export function Eyebrow({
  as = 'div',
  size = 'md',
  weight,
  gap = 'sm',
  className,
  ...rest
}: EyebrowProps) {
  const Tag = as as ElementType
  return (
    <Tag
      className={cls(
        'uppercase text-ink-500',
        SIZE[size],
        WEIGHT[weight ?? (size === 'sm' ? 'semibold' : 'medium')],
        GAP[gap],
        className,
      )}
      {...rest}
    />
  )
}
